// Forward the whole free-text query to the server as `search`. The Postgres v2
// endpoint matches it against the full-text index (search.project_index), whose
// search_doc already blends project name + developer + area and ranks results —
// so "terra casa", "emaar beachgate" etc. all resolve correctly server-side.
//
// A previous version tried to split a 2-word query into "<developer> <title>"
// here and send the first token as `developer_names`. But that filter needs an
// EXACT developer name, so a raw token like "terra" or "emaar" matched nothing
// and silently zeroed out the grid/map for any 2-word PROJECT name (e.g.
// "Terra Casa"). The server FTS makes that client-side guessing obsolete.
export function splitSearchQueryForResidentials(
  query: string | undefined,
  _hasExistingDeveloperFilter: boolean,
): { title?: string; developer_names?: string[] } {
  const q = query?.trim();
  if (!q) return {};
  return { title: q };
}

export type AutocompleteProjectRow = {
  id: string;
  title: string;
  image?: string;
  developer?: string;
};

function projectRelevanceScore(p: AutocompleteProjectRow, query: string): number {
  const q = query.trim().toLowerCase();
  const title = (p.title || '').trim().toLowerCase();
  const dev = (p.developer || '').trim().toLowerCase();
  const tokens = q.split(/\s+/).filter((t) => t.length >= 1);
  const hay = `${title} ${dev}`;

  let score = 0;
  if (title === q) score += 9000;
  else if (hay === q) score += 8500;
  else if (title.startsWith(q)) score += 8000;
  else if (hay.includes(q)) score += 7000;
  else if (title.includes(q)) score += 6500;

  for (const tok of tokens) {
    if (tok.length < 2) continue;
    const inTitle =
      title.includes(tok) || title.split(/[\s-]+/).some((w) => w.startsWith(tok));
    const inDev = dev.includes(tok) || dev.split(/[\s-]+/).some((w) => w.startsWith(tok));
    if (inTitle && inDev) score += 3500;
    else if (inTitle) score += 2200;
    else if (inDev) score += 1800;
    else score -= 400;
  }

  return score;
}

export function rankAutocompleteProjects<T extends AutocompleteProjectRow>(projects: T[], query: string): T[] {
  const q = query.trim();
  if (!q) return [...projects];
  return [...projects].sort((a, b) => {
    const diff = projectRelevanceScore(b, q) - projectRelevanceScore(a, q);
    if (diff !== 0) return diff;
    return a.title.localeCompare(b.title);
  });
}

export function mergeAutocompleteProjectsById<T extends AutocompleteProjectRow>(primary: T[], secondary: T[]): T[] {
  const map = new Map(primary.map((p) => [p.id, p]));
  for (const p of secondary) {
    if (!map.has(p.id)) map.set(p.id, p);
  }
  return [...map.values()];
}
